import type { FeatureGridBlockPayload } from '@opinix-cloud/shared';

interface PlatformProps {
  content: FeatureGridBlockPayload;
}

/**
 * "Build / Run / Review" three-step strip. Each item's `body` is a
 * paragraph followed by a short bullet list — bullets are encoded as
 * `\n• item` lines in the payload so the CMS author can edit them as
 * plain text. `iconKey` carries the step number ("01" / "02" / …).
 */
export function Platform({ content }: PlatformProps): JSX.Element {
  const items = content.items ?? [];
  return (
    <section id="platform" className="bg-steel-50 py-24 sm:py-32">
      <div className="container-x">
        <div className="max-w-2xl">
          {content.eyebrow && <p className="eyebrow">{content.eyebrow}</p>}
          {content.heading && (
            <h2 className="mt-3 section-title">{content.heading}</h2>
          )}
          {content.blurb && (
            <p className="mt-5 text-lg text-steel-600 leading-relaxed">
              {content.blurb}
            </p>
          )}
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((s, idx) => {
            const { paragraph, bullets } = splitBody(s.body);
            return (
              <div key={s.title} className="card flex flex-col">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs font-bold text-cyan-500 tracking-wider">
                    {s.iconKey ?? formatStepNumber(idx + 1)}
                  </span>
                  <h3 className="text-2xl font-extrabold tracking-tight text-navy-900">
                    {s.title}
                  </h3>
                </div>
                <p className="mt-4 text-steel-600 leading-relaxed">
                  {paragraph}
                </p>
                {bullets.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {bullets.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 text-sm text-steel-700"
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 20 20"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-ember-500"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z"
                          />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function formatStepNumber(n: number): string {
  return n.toString().padStart(2, '0');
}

function splitBody(body: string): { paragraph: string; bullets: string[] } {
  const lines = body.split('\n');
  const paragraph = lines[0] ?? '';
  const bullets = lines
    .slice(1)
    .map((l) => l.replace(/^[•\-*]\s*/, '').trim())
    .filter((l) => l.length > 0);
  return { paragraph, bullets };
}
